import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { NormalizedProfile } from '@/types/profile';

export interface LatheBuildOptions {
  /** Number of segments around the circumference. Higher = smoother, heavier mesh. */
  radialSegments?: number;
}

export interface LatheBuildResult {
  geometry: THREE.BufferGeometry;
  /** Total length of the bat along its axis, in mm. */
  lengthMm: number;
}

const DEFAULT_RADIAL_SEGMENTS = 96;
const MIN_CAP_RADIUS = 1e-3; // below this, treat the end as already closed (a point)

/**
 * Builds a solid, watertight, smooth-shaded bat mesh from a normalized
 * (mm, position-ascending, position starting at 0) profile.
 *
 * Three.js LatheGeometry revolves 2D points (x = radius, y = height) around
 * the Y axis. We build in that space, add flat end caps (a real bat's knob
 * base and barrel tip are flat, not pointed), then rotate + center the
 * result so the finished mesh lies along the X axis with its origin at the
 * bat's centroid, matching the app's scene convention.
 */
export function buildLatheGeometry(
  profile: NormalizedProfile,
  options: LatheBuildOptions = {},
): LatheBuildResult {
  const radialSegments = options.radialSegments ?? DEFAULT_RADIAL_SEGMENTS;

  if (profile.points.length < 2) {
    throw new Error('At least two profile points are required to build geometry.');
  }

  const lathePoints = profile.points.map(
    (p) => new THREE.Vector2(Math.max(p.radius, 0), p.position),
  );

  const lathe = new THREE.LatheGeometry(lathePoints, radialSegments);
  lathe.computeVertexNormals();

  const parts: THREE.BufferGeometry[] = [lathe];

  const first = profile.points[0];
  const last = profile.points[profile.points.length - 1];

  if (first.radius > MIN_CAP_RADIUS) {
    const knobCap = new THREE.CircleGeometry(first.radius, radialSegments);
    // CircleGeometry is built in the XY plane facing +Z; rotate to face -Y
    // (outward from the knob end) and translate into position.
    knobCap.rotateX(Math.PI / 2);
    knobCap.rotateY(Math.PI); // flip normal to face away from the bat body
    knobCap.translate(0, first.position, 0);
    knobCap.computeVertexNormals();
    parts.push(knobCap);
  }

  if (last.radius > MIN_CAP_RADIUS) {
    const tipCap = new THREE.CircleGeometry(last.radius, radialSegments);
    tipCap.rotateX(-Math.PI / 2);
    tipCap.translate(0, last.position, 0);
    tipCap.computeVertexNormals();
    parts.push(tipCap);
  }

  const merged = mergeGeometries(parts, false) ?? lathe;
  merged.computeVertexNormals();
  merged.computeBoundingBox();

  // Reorient: lathe axis (Y) -> scene axis (X), then center on the geometry's
  // own bounding box so the model is centered at the origin for framing,
  // clipping, and export consistency.
  merged.rotateZ(Math.PI / 2); // Y-axis profile now runs along -X; correct below
  merged.rotateY(Math.PI); // ensure knob-to-tip reads left-to-right (+X)
  merged.computeBoundingBox();

  const box = merged.boundingBox!;
  const center = new THREE.Vector3();
  box.getCenter(center);
  merged.translate(-center.x, -center.y, -center.z);
  merged.computeBoundingBox();
  merged.computeVertexNormals();

  const lengthMm = last.position - first.position;

  return { geometry: merged, lengthMm };
}
